/**
 * The displays' work areas a pin lands on, in logical points. A display an
 * overlay runs on has published its work area (`floating-frame-displays.ts`,
 * exact at mixed DPI); otherwise Tauri's monitors stand in, converted by
 * `logicalPinRect` like the pin window's own frame.
 */
import {
  availableMonitors,
  primaryMonitor,
  type Monitor,
} from "@tauri-apps/api/window";
import {
  floatingFrameDisplayForPoint,
  getFloatingFrameDisplayArea,
} from "@/desktop/floating-frame-displays";
import type { PinRect } from "./desktop-pin-contract";

export interface PinDisplay {
  workArea: PinRect;
  primary: boolean;
}

/**
 * A physical rect from Tauri in the contract's logical points, divided by
 * the scale factor Tauri multiplied it with: a monitor's own, or for a
 * window the scale of the display it is on. On macOS that is exactly the
 * global NSScreen point space at any mix of scales (Tauri converts both from
 * points). On Windows the physical desktop is one pixel space, so at mixed
 * DPI the logical rects of displays with different scales do not line up
 * exactly (plan 12 §7).
 */
export function logicalPinRect(
  position: { x: number; y: number },
  size: { width: number; height: number },
  scaleFactor: number,
): PinRect {
  const scale = scaleFactor > 0 ? scaleFactor : 1;
  return {
    x: Math.round(position.x / scale),
    y: Math.round(position.y / scale),
    width: Math.round(size.width / scale),
    height: Math.round(size.height / scale),
  };
}

function sameMonitor(a: Monitor, b: Monitor): boolean {
  return (
    a.name === b.name &&
    a.position.x === b.position.x &&
    a.position.y === b.position.y
  );
}

/** Every connected display's work area; none when they cannot be read. */
export async function pinDisplays(): Promise<PinDisplay[]> {
  try {
    const [monitors, primary] = await Promise.all([
      availableMonitors(),
      primaryMonitor(),
    ]);
    return monitors.map((monitor, index) => ({
      workArea: logicalPinRect(
        monitor.workArea.position,
        monitor.workArea.size,
        monitor.scaleFactor,
      ),
      primary: primary ? sameMonitor(monitor, primary) : index === 0,
    }));
  } catch {
    return [];
  }
}

/** The main display: the primary one, else the first. */
export function mainPinDisplay(
  displays: readonly PinDisplay[],
): PinDisplay | undefined {
  return displays.find((display) => display.primary) ?? displays[0];
}

/** The display holding the rect's centre. */
export function pinDisplayHolding(
  displays: readonly PinDisplay[],
  rect: PinRect,
): PinDisplay | undefined {
  const x = rect.x + rect.width / 2;
  const y = rect.y + rect.height / 2;
  return displays.find(
    ({ workArea }) =>
      x >= workArea.x &&
      x < workArea.x + workArea.width &&
      y >= workArea.y &&
      y < workArea.y + workArea.height,
  );
}

/**
 * Display `displayId`'s work area; one this window has not heard is the
 * display holding `anchor`; else the main display's.
 */
export async function pinWorkArea(
  displayId: number | null,
  anchor: { x: number; y: number } | null,
): Promise<PinRect | null> {
  const published =
    displayId === null ? undefined : getFloatingFrameDisplayArea(displayId);
  if (published) return published;
  const displays = await pinDisplays();
  return anchor
    ? workAreaHolding(displays, { ...anchor, width: 0, height: 0 })
    : (mainPinDisplay(displays)?.workArea ?? null);
}

/** The work area of the display holding `rect`, else the main display's. */
export function workAreaHolding(
  displays: readonly PinDisplay[],
  rect: PinRect,
): PinRect | null {
  return (
    (pinDisplayHolding(displays, rect) ?? mainPinDisplay(displays))?.workArea ??
    null
  );
}

/** `workAreaHolding` on the displays connected now. */
export async function pinWorkAreaHolding(
  rect: PinRect,
): Promise<PinRect | null> {
  return workAreaHolding(await pinDisplays(), rect);
}

/** The display under a pin's centre; unknown, `fallback`. */
export function pinDisplayIdAt(
  rect: PinRect,
  fallback: number | null,
): number | null {
  return (
    floatingFrameDisplayForPoint(
      { x: rect.x + rect.width / 2, y: rect.y + rect.height / 2 },
      fallback ?? -1,
    )?.displayId ?? fallback
  );
}
